import React, { useState } from 'react';
import {
    Card,
    CardContent,
    CardHeader,
    TextField,
    Button,
    Stack,
    Typography,
    CircularProgress,
    IconButton,
    InputAdornment,
    FormControl,
    InputLabel,
    Input
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useContent } from '../../../../context/ContentContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';


const ProfileDisplay = () => {
    const { userData, updateUserData, loading, error } = useContent(); // Assume userData is preloaded
    const [profile, setProfile] = useState({
        name: userData?.name || '',
        nik: userData?.nik || '',
        email: userData?.email || '',
        address: userData?.address || '',
        phone: userData?.phone || '',
        profesion: userData?.profesion || '', // Corrected to profesion
        ktp: null,
        kartu_nama: null,
        password: '',
        password_confirmation: '',
    });
    const [showPassword, setShowPassword] = useState(false); // State to toggle password visibility
    const [showConfirmPassword, setShowConfirmPassword] = useState(false); // State to toggle confirm password visibility
    const [formErrors, setFormErrors] = useState({}); // For validation errors

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setProfile({ ...profile, [name]: value });
    };

    const handleFileChange = (e) => {
        const { name, files } = e.target;
        if (files.length > 0) {
            setProfile({ ...profile, [name]: files[0] });
        } else {
            setProfile({ ...profile, [name]: null }); // Reset file field if no file is selected
        }
    };

    const validateForm = () => {
        const errors = {};

        // Validate Name (required)
        if (!profile.name.trim()) {
            errors.name = 'Nama wajib diisi.';
        }

        // Validate NIK (Numeric and Length 16)
        if (!/^\d{16}$/.test(profile.nik)) {
            errors.nik = 'NIK harus berupa 16 digit angka.';
        }

        // Validate Phone (Only Numbers)
        if (!/^\d+$/.test(profile.phone)) {
            errors.phone = 'Nomor telepon hanya boleh berisi angka.';
        }

        // Validate Email (Simple Regex)
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
        if (!emailRegex.test(profile.email)) {
            errors.email = 'Alamat email tidak valid.';
        }

        // Validate Password (minimum length)
        if (!profile.password.trim()) {
            errors.password = 'Kata Sandi wajib diisi.';
        } else if (profile.password.trim().length < 6) {
            errors.password = 'Kata Sandi harus memiliki minimal 6 karakter.';
        }

        // Validate Password and Confirm Password
        if (profile.password.trim() !== profile.password_confirmation.trim()) {
            errors.password_confirmation = 'Kata Sandi dan Konfirmasi Kata Sandi tidak cocok.';
        }

        // Validate Address (required)
        if (!profile.address.trim()) {
            errors.address = 'Alamat wajib diisi.';
        }

        // Validate Profesi (required)
        if (!profile.profesion.trim()) {
            errors.profesion = 'Profesi wajib diisi.';
        }

        // Validate KTP (file required)
        if (!profile.ktp) {
            errors.ktp = 'KTP wajib diunggah.';
        }

        // Validate Business Card (file required)
        if (!profile.kartu_nama) {
            errors.kartu_nama = 'Kartu Nama wajib diunggah.';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0; // Return true if no errors
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }

        const formData = new FormData();
        formData.append('name', profile.name);
        formData.append('nik', profile.nik);
        formData.append('email', profile.email);
        formData.append('password', profile.password);
        formData.append('password_confirmation', profile.password_confirmation);
        formData.append('address', profile.address);
        formData.append('phone', profile.phone);
        formData.append('profesion', profile.profesion);

        if (profile.ktp) formData.append('ktp', profile.ktp); // Attach file if present
        if (profile.kartu_nama) formData.append('kartu_nama', profile.kartu_nama); // Attach file if present

        try {
            console.log('FormData to be sent:', formData);
            await updateUserData(formData);
            toast.success('Profil berhasil diperbarui!', {
                position: 'top-right',
                autoClose: 3000,
            }); // Trigger success toast
        } catch (error) {
            console.error('Failed to update profile:', error);
        }
    };

    const getFieldError = (field) => {
        return formErrors[field] || ''; // Return the error message from formErrors state
    };

    return (
        <Card sx={{ maxWidth: '600px', mx: 'auto', mt: 4, boxShadow: 3, borderRadius: 2 }}>
            <CardHeader title="Informasi Dasar" />
            <CardContent>
                <Typography variant="body2" color="text.secondary" mb={2}>
                    Silakan lengkapi informasi Anda, kami akan menggunakannya untuk validasi saat Anda ingin meminta penarikan.
                </Typography>
                <form onSubmit={handleSubmit}>
                    <Stack spacing={2}>
                        <TextField
                            label="Nama"
                            name="name"
                            value={profile.name}
                            onChange={handleInputChange}
                            fullWidth
                            error={!!formErrors.name}
                            helperText={getFieldError('name')}
                            disabled={loading}
                        />
                        <TextField
                            label="NIK"
                            name="nik"
                            value={profile.nik}
                            onChange={handleInputChange}
                            fullWidth
                            error={!!formErrors.nik}
                            helperText={getFieldError('nik')}
                            disabled={loading}
                        />
                        <TextField
                            label="Email"
                            name="email"
                            value={profile.email}
                            onChange={handleInputChange}
                            fullWidth
                            error={!!formErrors.email}
                            helperText={getFieldError('email')}
                            disabled={loading}
                        />
                        <TextField
                            label="Kata Sandi"
                            name="password"
                            type={showPassword ? 'text' : 'password'} // Toggle visibility
                            value={profile.password}
                            onChange={handleInputChange}
                            fullWidth
                            error={!!formErrors.password}
                            helperText={getFieldError('password')}
                            disabled={loading}
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowPassword(!showPassword)}>
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                        <TextField
                            label="Konfirmasi Kata Sandi"
                            name="password_confirmation"
                            type={showConfirmPassword ? 'text' : 'password'} // Toggle visibility
                            value={profile.password_confirmation}
                            onChange={handleInputChange}
                            fullWidth
                            error={!!formErrors.password_confirmation}
                            helperText={getFieldError('password_confirmation')}
                            disabled={loading}
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                        <TextField
                            label="Alamat"
                            name="address"
                            value={profile.address}
                            onChange={handleInputChange}
                            fullWidth
                            error={!!formErrors.address}
                            helperText={getFieldError('address')}
                            disabled={loading}
                        />
                        <TextField
                            label="Telepon"
                            name="phone"
                            value={profile.phone}
                            onChange={handleInputChange}
                            fullWidth
                            error={!!formErrors.phone}
                            helperText={getFieldError('phone')}
                            disabled={loading}
                        />
                        <TextField
                            label="Profesi"
                            name="profesion"  // Ensure the name matches the state key
                            value={profile.profesion || ''}  // Ensure the value is correctly passed from the state
                            onChange={handleInputChange}
                            fullWidth
                            error={!!formErrors.profesion}
                            helperText={getFieldError('profesion')}
                            disabled={loading}
                        />
                        <FormControl>
                            <InputLabel shrink>KTP</InputLabel>
                            <Input
                                type="file"
                                name="ktp"
                                onChange={handleFileChange}
                                fullWidth
                                error={!!formErrors.ktp}
                                disabled={loading}
                            />
                            {formErrors.ktp && <Typography color="error">{getFieldError('ktp')}</Typography>}
                        </FormControl>
                        <FormControl>
                            <InputLabel shrink>Kartu Nama</InputLabel>
                            <Input
                                type="file"
                                name="kartu_nama"  // Updated to 'kartu_nama'
                                onChange={handleFileChange}
                                fullWidth
                                error={!!formErrors.kartu_nama}  // Updated error check
                                disabled={loading}
                            />
                            {formErrors.kartu_nama && <Typography color="error">{getFieldError('kartu_nama')}</Typography>}
                        </FormControl>
                        <Button
                            variant="contained"
                            color="primary"
                            type="submit" // Set the button type to 'submit'
                            fullWidth
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : 'Simpan'}
                        </Button>
                    </Stack>
                </form>
                <ToastContainer />
            </CardContent>
        </Card>
    );
};

export default ProfileDisplay;
